import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Button, Datagrid, Dialog, Loader, MoneyInput, useConfirm } from '@refera/ui-web'
import { Danger as DangerIcon } from '@refera/ui-icons'
import SearchIcon from '@material-ui/icons/Search'

import { navigate } from '@reach/router'

import {
  paymentConfirmationColumns,
  FINANCE_ASAAS_PAYMENT_METHODS_TOGGLE,
  PAYMENT_METHOD_VALUES,
  getPaymentMethodOptions,
  shouldShowAsaasPaymentMethods,
  formatArrayToString,
} from '../utils/constants'
import { FINANCE_ROUTES, ROUTES } from '_/utils/constants'

import HeaderTitle from '_/components/header-title'
import ConfirmDateAndPaymentModal from '../components/ConfirmDateAndPaymentModal'

import {
  getBudgetInstallmentDiscount,
  getVerifyInstallmentsStepStatus,
  payBudgetInstallments,
  removeBudgetInstallments,
  saveBudgetInstallmentDiscount,
} from '_/modules/finance/actions'
import { getSystemParameters } from '_/modules/service-orders/actions'
import { userSelector } from '_/modules/authentication/selectors'
import { getSystemParametersSelector } from '_/modules/service-orders/selectors'
import ConfirmRemoveDialog from '_components/dialogs/ConfirmRemoveDialog'

import useStyles from '../styles'
import { formatCurrency } from '_/utils/helpers'
import { formatErrorMessage } from '../../utils/FormatErrorMessage'
import { useToast } from '_/hooks/use-toast'
import SearchBar from '_components/search-bar'

const defaultInfoDialog = {
  isOpen: false,
  icon: '',
  type: '',
  subject: '',
  description: '',
}

const defaultDiscountModalForm = {
  anticipationTax: null,
  anticipationTaxReason: '',
  monthlyDiscount: null,
  monthlyDiscountReason: '',
  reimbursementDiscount: null,
  reimbursementDiscountReason: '',
}

const PaymentConfirmation = ({
  menuType,
  toggleMenu,
  selectedIds,
  items,
  handleSuccessPayment,
  fromAnticipations,
}) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState(selectedIds || [])
  const [dialogMessage, setDialogMessage] = useState()
  const [infoDialog, setInfoDialog] = useState(defaultInfoDialog)
  const [isLoading, setIsLoading] = useState(false)

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [serviceOrderIdSearch, setServiceOrderIdSearch] = useState('')
  const [discountByInstallmentId, setDiscountByInstallmentId] = useState({})
  const [discountModal, setDiscountModal] = useState({ open: false, installmentId: null })
  const [discountModalForm, setDiscountModalForm] = useState(defaultDiscountModalForm)
  const [discountModalError, setDiscountModalError] = useState('')
  const [isSavingDiscount, setIsSavingDiscount] = useState(false)
  const [isLoadingDiscountModalData, setIsLoadingDiscountModalData] = useState(false)
  const discountModalRequestRef = useRef(0)
  const activeDiscountInstallmentRef = useRef(null)

  const { showToast } = useToast()

  const { isConfirmed } = useConfirm()

  const user = useSelector(userSelector)
  const systemParameters = useSelector(getSystemParametersSelector)
  const showAntecipation = user?.activeProfile?.resourceAntecipation
  const isAsaasPaymentMethodsToggleEnabled = useMemo(
    () =>
      Boolean(
        systemParameters?.find(param => param.name === FINANCE_ASAAS_PAYMENT_METHODS_TOGGLE)
          ?.isActive
      ),
    [systemParameters]
  )
  const shouldIncludeAsaasPaymentMethods = useMemo(
    () =>
      shouldShowAsaasPaymentMethods({
        user,
        isFeatureEnabled: isAsaasPaymentMethodsToggleEnabled,
      }),
    [isAsaasPaymentMethodsToggleEnabled, user]
  )
  const paymentMethodOptions = useMemo(
    () =>
      getPaymentMethodOptions({
        fromAnticipations: Boolean(fromAnticipations),
        includeAsaasMethods: shouldIncludeAsaasPaymentMethods,
      }),
    [fromAnticipations, shouldIncludeAsaasPaymentMethods]
  )
  const batchMethods = useMemo(
    () =>
      fromAnticipations
        ? [PAYMENT_METHOD_VALUES.TED_SANTANDER, PAYMENT_METHOD_VALUES.CONDOLIVRE]
        : [PAYMENT_METHOD_VALUES.TED_SANTANDER],
    [fromAnticipations]
  )

  const searchInputProps = useMemo(
    () => ({
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon className={styles.confirmationSearchIcon} />
        </InputAdornment>
      ),
    }),
    []
  )

  const handleConfirmModal = async () => {
    if (menuType === 'pay') return setModalOpen(true)
    if (menuType === 'remove') {
      setDialogMessage({
        message: 'Você tem certeza que deseja remover o pagamento dos itens selecionados?',
        type: 'remove',
      })

      const confirmed = await isConfirmed()

      if (confirmed) {
        return handleRemovePayment()
      }
    }
    return undefined
  }

  const payInstallments = async ({ payload }) => {
    setIsLoading(true)

    await Promise.resolve(dispatch(payBudgetInstallments(payload)))
      .then(res => {
        setModalOpen(false)
        setIsLoading(false)

        const { batch = '', errorMessage = '' } = res
        // redirect to batch if payment creates batch or go back
        batchMethods.includes(payload.paymentMethod)
          ? navigate(`${FINANCE_ROUTES.BATCHES}/${batch?.id}`, {
              state: { created: true, info: errorMessage },
            })
          : handleSuccessPayment(true)
      })
      .catch(async err => {
        setIsLoading(false)
        const error = formatErrorMessage(err)

        if (error?.includes('Você deseja continuar')) {
          setDialogMessage({
            message: error,
            type: 'info',
          })

          const confirmed = await isConfirmed()
          if (confirmed) {
            const newPayload = { ...payload, forcePayment: true }
            return payInstallments({ payload: newPayload })
          }
        } else {
          handleSuccessPayment(false, error)
        }
        return null
      })
  }

  const selectedItemsSet = useMemo(
    () => new Set(selectedItems.map(item => `${item}`)),
    [selectedItems]
  )
  const selectedDiscountInstallmentIds = useMemo(
    () =>
      Array.from(new Set([...(selectedIds || []), ...selectedItems]))
        .map(item => `${item}`)
        .filter(item => item !== 'null' && item !== 'undefined' && item !== '')
        .sort((firstItem, secondItem) => firstItem.localeCompare(secondItem)),
    [selectedIds, selectedItems]
  )
  const selectedDiscountInstallmentIdsKey = useMemo(
    () => selectedDiscountInstallmentIds.join('|'),
    [selectedDiscountInstallmentIds]
  )
  const itemsSignature = useMemo(
    () => items.map(item => `${item.id}:${Boolean(item.isSaasServiceOrder)}`).join('|'),
    [items]
  )
  const parseDiscountInputValue = useCallback(value => {
    if (value === null || value === undefined || value === '') return 0
    return Number(value) || 0
  }, [])
  const isDiscountEnabledForInstallment = useCallback(
    installment => Boolean(installment) && !installment?.isSaasServiceOrder,
    []
  )
  const discountReasonValidation = useMemo(() => {
    const anticipationTax = parseDiscountInputValue(discountModalForm.anticipationTax)
    const monthlyDiscount = parseDiscountInputValue(discountModalForm.monthlyDiscount)
    const reimbursementDiscount = parseDiscountInputValue(discountModalForm.reimbursementDiscount)

    const required = {
      anticipationTaxReason: anticipationTax > 0,
      monthlyDiscountReason: monthlyDiscount > 0,
      reimbursementDiscountReason: reimbursementDiscount > 0,
    }

    const errors = {
      anticipationTaxReason:
        required.anticipationTaxReason && !discountModalForm.anticipationTaxReason.trim(),
      monthlyDiscountReason:
        required.monthlyDiscountReason && !discountModalForm.monthlyDiscountReason.trim(),
      reimbursementDiscountReason:
        required.reimbursementDiscountReason &&
        !discountModalForm.reimbursementDiscountReason.trim(),
    }

    return { required, errors }
  }, [discountModalForm, parseDiscountInputValue])

  const getExistingDiscountData = useCallback(
    item => ({
      anticipationTax: Number(
        item?.anticipationTax ?? item?.paymentInstallment?.anticipationTax ?? 0
      ),
      monthlyDiscount: Number(item?.monthlyDiscount ?? 0),
      reimbursementDiscount: Number(item?.reimbursementDiscount ?? 0),
      anticipationTaxReason: '',
      monthlyDiscountReason: '',
      reimbursementDiscountReason: '',
    }),
    []
  )

  const getDiscountDataByInstallment = useCallback(
    item => {
      if (!isDiscountEnabledForInstallment(item)) {
        return {
          anticipationTax: 0,
          anticipationTaxReason: '',
          monthlyDiscount: 0,
          monthlyDiscountReason: '',
          reimbursementDiscount: 0,
          reimbursementDiscountReason: '',
        }
      }
      return discountByInstallmentId[`${item.id}`] || getExistingDiscountData(item)
    },
    [discountByInstallmentId, getExistingDiscountData, isDiscountEnabledForInstallment]
  )

  const itemsWithDiscounts = useMemo(
    () =>
      items.map(item => {
        const discountData = getDiscountDataByInstallment(item)
        const anticipationTax = Number(discountData.anticipationTax || 0)
        const monthlyDiscount = Number(discountData.monthlyDiscount || 0)
        const reimbursementDiscount = Number(discountData.reimbursementDiscount || 0)
        const providerValue = Number(item.providerValue || 0)
        const netValue = Number(
          (providerValue - anticipationTax - monthlyDiscount - reimbursementDiscount).toFixed(2)
        )

        return {
          ...item,
          anticipationTax,
          monthlyDiscount,
          reimbursementDiscount,
          value: netValue,
        }
      }),
    [getDiscountDataByInstallment, items]
  )

  const selectedInstallments = useMemo(
    () => itemsWithDiscounts.filter(item => selectedItemsSet.has(`${item.id}`)),
    [itemsWithDiscounts, selectedItemsSet]
  )

  const hasInvalidDiscount = useMemo(
    () =>
      selectedInstallments.some(item => {
        const discountData = getDiscountDataByInstallment(item)
        const anticipationTax = Number(discountData.anticipationTax || 0)
        const monthlyDiscount = Number(discountData.monthlyDiscount || 0)
        const reimbursementDiscount = Number(discountData.reimbursementDiscount || 0)
        const hasNegative = anticipationTax < 0 || monthlyDiscount < 0 || reimbursementDiscount < 0
        const totalDiscountApplied = anticipationTax + monthlyDiscount + reimbursementDiscount
        const netValue = Number(item.providerValue || 0) - totalDiscountApplied
        return hasNegative || netValue <= 0
      }),
    [getDiscountDataByInstallment, selectedInstallments]
  )

  const hasMissingDiscountReason = useMemo(
    () =>
      selectedInstallments.some(item => {
        const discountData = discountByInstallmentId[`${item.id}`]
        if (!discountData) return false

        return (
          (Number(discountData.anticipationTax || 0) > 0 &&
            !`${discountData.anticipationTaxReason || ''}`.trim()) ||
          (Number(discountData.monthlyDiscount || 0) > 0 &&
            !`${discountData.monthlyDiscountReason || ''}`.trim()) ||
          (Number(discountData.reimbursementDiscount || 0) > 0 &&
            !`${discountData.reimbursementDiscountReason || ''}`.trim())
        )
      }),
    [discountByInstallmentId, selectedInstallments]
  )

  const handleOpenDiscountModal = useCallback(
    async row => {
      if (!isDiscountEnabledForInstallment(row)) {
        showToast({
          message: 'Desconto não está disponível para chamados de origem SAAS.',
          type: 'error',
        })
        return
      }

      const discountData = getDiscountDataByInstallment(row)

      setDiscountModal({ open: true, installmentId: row.id })
      setDiscountModalForm({
        anticipationTax: Number(discountData.anticipationTax || 0) || null,
        anticipationTaxReason: discountData.anticipationTaxReason || '',
        monthlyDiscount: Number(discountData.monthlyDiscount || 0) || null,
        monthlyDiscountReason: discountData.monthlyDiscountReason || '',
        reimbursementDiscount: Number(discountData.reimbursementDiscount || 0) || null,
        reimbursementDiscountReason: discountData.reimbursementDiscountReason || '',
      })
      setDiscountModalError('')
      const requestId = discountModalRequestRef.current + 1
      discountModalRequestRef.current = requestId
      activeDiscountInstallmentRef.current = row.id
      setIsLoadingDiscountModalData(true)

      try {
        const persistedDiscount = await dispatch(getBudgetInstallmentDiscount(row.id))
        const isCurrentModalRequest =
          discountModalRequestRef.current === requestId &&
          activeDiscountInstallmentRef.current === row.id
        if (!isCurrentModalRequest) return

        const nextForm = {
          anticipationTax: Number(persistedDiscount?.anticipationTax || 0) || null,
          anticipationTaxReason: persistedDiscount?.anticipationTaxReason || '',
          monthlyDiscount: Number(persistedDiscount?.monthlyDiscount || 0) || null,
          monthlyDiscountReason: persistedDiscount?.monthlyDiscountReason || '',
          reimbursementDiscount: Number(persistedDiscount?.reimbursementDiscount || 0) || null,
          reimbursementDiscountReason: persistedDiscount?.reimbursementDiscountReason || '',
        }

        setDiscountModalForm(nextForm)
        setDiscountByInstallmentId(prevState => ({
          ...prevState,
          [`${row.id}`]: {
            anticipationTax: Number(persistedDiscount?.anticipationTax || 0),
            anticipationTaxReason: persistedDiscount?.anticipationTaxReason || '',
            monthlyDiscount: Number(persistedDiscount?.monthlyDiscount || 0),
            monthlyDiscountReason: persistedDiscount?.monthlyDiscountReason || '',
            reimbursementDiscount: Number(persistedDiscount?.reimbursementDiscount || 0),
            reimbursementDiscountReason: persistedDiscount?.reimbursementDiscountReason || '',
          },
        }))
      } catch (error) {
        const isCurrentModalRequest =
          discountModalRequestRef.current === requestId &&
          activeDiscountInstallmentRef.current === row.id
        if (!isCurrentModalRequest) return

        showToast({
          message: formatErrorMessage(error),
          type: 'error',
        })
      } finally {
        const isCurrentModalRequest =
          discountModalRequestRef.current === requestId &&
          activeDiscountInstallmentRef.current === row.id
        if (isCurrentModalRequest) {
          setIsLoadingDiscountModalData(false)
        }
      }
    },
    [dispatch, getDiscountDataByInstallment, isDiscountEnabledForInstallment, showToast]
  )

  const handleCloseDiscountModal = useCallback(() => {
    discountModalRequestRef.current += 1
    activeDiscountInstallmentRef.current = null
    setIsLoadingDiscountModalData(false)
    setDiscountModal({ open: false, installmentId: null })
    setDiscountModalForm(defaultDiscountModalForm)
    setDiscountModalError('')
  }, [])

  const handleSaveDiscountModal = useCallback(async () => {
    const installment = itemsWithDiscounts.find(item => item.id === discountModal.installmentId)
    if (!installment) {
      handleCloseDiscountModal()
      return
    }

    const anticipationTax = parseDiscountInputValue(discountModalForm.anticipationTax)
    const monthlyDiscount = parseDiscountInputValue(discountModalForm.monthlyDiscount)
    const reimbursementDiscount = parseDiscountInputValue(discountModalForm.reimbursementDiscount)

    if (anticipationTax < 0 || monthlyDiscount < 0 || reimbursementDiscount < 0) {
      setDiscountModalError('Não é permitido informar valores negativos.')
      return
    }

    if (
      discountReasonValidation.errors.anticipationTaxReason ||
      discountReasonValidation.errors.monthlyDiscountReason ||
      discountReasonValidation.errors.reimbursementDiscountReason
    ) {
      setDiscountModalError('Preencha os motivos obrigatórios dos descontos informados.')
      return
    }

    const totalDiscountApplied = anticipationTax + monthlyDiscount + reimbursementDiscount
    const providerValue = Number(installment.providerValue || 0)

    if (totalDiscountApplied >= providerValue) {
      setDiscountModalError(
        'A soma dos descontos deve ser menor que o valor líquido a receber da parcela.'
      )
      return
    }

    try {
      setIsSavingDiscount(true)
      await dispatch(
        saveBudgetInstallmentDiscount(discountModal.installmentId, {
          anticipationTax,
          anticipationTaxReason: discountModalForm.anticipationTaxReason.trim(),
          monthlyDiscount,
          monthlyDiscountReason: discountModalForm.monthlyDiscountReason.trim(),
          reimbursementDiscount,
          reimbursementDiscountReason: discountModalForm.reimbursementDiscountReason.trim(),
        })
      )

      setDiscountByInstallmentId(prevState => ({
        ...prevState,
        [discountModal.installmentId]: {
          anticipationTax,
          anticipationTaxReason: discountModalForm.anticipationTaxReason.trim(),
          monthlyDiscount,
          monthlyDiscountReason: discountModalForm.monthlyDiscountReason.trim(),
          reimbursementDiscount,
          reimbursementDiscountReason: discountModalForm.reimbursementDiscountReason.trim(),
        },
      }))

      showToast({
        message: 'Desconto salvo com sucesso.',
        type: 'success',
      })
      handleCloseDiscountModal()
    } catch (error) {
      setDiscountModalError(formatErrorMessage(error))
    } finally {
      setIsSavingDiscount(false)
    }
  }, [
    dispatch,
    discountModal.installmentId,
    discountModalForm,
    discountReasonValidation.errors.anticipationTaxReason,
    discountReasonValidation.errors.monthlyDiscountReason,
    discountReasonValidation.errors.reimbursementDiscountReason,
    handleCloseDiscountModal,
    itemsWithDiscounts,
    parseDiscountInputValue,
    showToast,
  ])

  const handleConfirmPayment = useCallback(
    data => {
      const selectedItemsByStringSet = new Set(selectedItems.map(item => `${item}`))
      const idsToPay = selectedIds.filter(id => selectedItemsByStringSet.has(`${id}`))
      const discounts = idsToPay
        .map(id => {
          const item = itemsWithDiscounts.find(installment => `${installment.id}` === `${id}`)
          if (!item) return null
          if (!isDiscountEnabledForInstallment(item)) return null

          const discountData = getDiscountDataByInstallment(item)
          const anticipationTax = Number(discountData?.anticipationTax || 0)
          const monthlyDiscount = Number(discountData?.monthlyDiscount || 0)
          const reimbursementDiscount = Number(discountData?.reimbursementDiscount || 0)
          const reasonMessages = [
            discountData?.anticipationTaxReason
              ? `Taxa de antecipação: ${discountData.anticipationTaxReason}`
              : '',
            discountData?.monthlyDiscountReason
              ? `Desconto mensalidade: ${discountData.monthlyDiscountReason}`
              : '',
            discountData?.reimbursementDiscountReason
              ? `Desconto reembolso: ${discountData.reimbursementDiscountReason}`
              : '',
          ].filter(Boolean)

          const hasAnyReason = reasonMessages.length > 0
          if (!anticipationTax && !monthlyDiscount && !reimbursementDiscount && !hasAnyReason)
            return null

          return {
            installmentId: id,
            value: monthlyDiscount + reimbursementDiscount,
            reason: reasonMessages.join(' | '),
            anticipationTax,
            anticipationTaxReason: discountData?.anticipationTaxReason || '',
            monthlyDiscount,
            monthlyDiscountReason: discountData?.monthlyDiscountReason || '',
            reimbursementDiscount,
            reimbursementDiscountReason: discountData?.reimbursementDiscountReason || '',
          }
        })
        .filter(Boolean)

      const payload = {
        ids: idsToPay,
        paymentMethod: data.paymentMethod,
        datetimePaid: data.datetimePayment.toISOString(),
        screen: fromAnticipations ? 'anticipations' : 'installments',
        discounts,
      }

      if (data.paymentMethod === PAYMENT_METHOD_VALUES.TED_SANTANDER) {
        // TED - SANTANDER exige pagamento em lote com duas ou mais parcelas.
        if (idsToPay.length < 2) {
          setInfoDialog({
            isOpen: true,
            icon: DangerIcon,
            type: 'error',
            subject: 'Erro',
            description:
              'Não é possível processeguir com a ação. Para pagar via TED - SANTANDER é necessário pagar duas ou mais parcelas',
          })
          return
        }
      }

      if (hasInvalidDiscount || hasMissingDiscountReason) {
        setInfoDialog({
          isOpen: true,
          icon: DangerIcon,
          type: 'error',
          subject: 'Erro',
          description: hasMissingDiscountReason
            ? 'Informe o motivo para todos os descontos aplicados.'
            : 'O desconto aplicado não pode ser maior que o total líquido a receber.',
        })
        return
      }

      payInstallments({ payload })
    },
    [
      fromAnticipations,
      getDiscountDataByInstallment,
      hasInvalidDiscount,
      hasMissingDiscountReason,
      isDiscountEnabledForInstallment,
      itemsWithDiscounts,
      payInstallments,
      selectedIds,
      selectedItems,
    ]
  )

  const handleRemovePayment = () => {
    setIsLoading(true)
    Promise.resolve(dispatch(removeBudgetInstallments({ ids: selectedIds })))
      .then(() => {
        setModalOpen(false)
        handleSuccessPayment(true)
      })
      .catch(err => {
        console.warn('error', err)
        handleSuccessPayment(false, formatErrorMessage(err) || '')
        setIsLoading(false)
      })
  }

  const totalMenuValues = useMemo(() => {
    const totalDiscounts = selectedInstallments.reduce((acc, item) => {
      const discountData = getDiscountDataByInstallment(item)
      return (
        acc +
        Number(discountData?.monthlyDiscount || 0) +
        Number(discountData?.reimbursementDiscount || 0)
      )
    }, 0)

    const totalPrice = selectedInstallments.reduce(
      (acc, item) => acc + Number(item.providerValue || 0),
      0
    )

    const totalNetValue = selectedInstallments.reduce(
      (acc, item) => acc + Number(item.value || 0),
      0
    )

    const totalInterest = selectedInstallments.reduce(
      (acc, item) => acc + Number(item.anticipationTax || 0),
      0
    )

    const totalDiscountsValue = formatCurrency(totalDiscounts)
    const totalPriceServiceProvider = formatCurrency(totalPrice)
    const totalValue = formatCurrency(totalNetValue)
    const interestValue = formatCurrency(totalInterest)
    return { totalPriceServiceProvider, totalValue, interestValue, totalDiscountsValue }
  }, [getDiscountDataByInstallment, selectedInstallments])

  const filteredItems = useMemo(() => {
    const normalizedSearch = serviceOrderIdSearch.trim()

    if (!normalizedSearch) return itemsWithDiscounts

    return itemsWithDiscounts.filter(item =>
      `${item.serviceOrderId || ''}`.includes(normalizedSearch)
    )
  }, [itemsWithDiscounts, serviceOrderIdSearch])
  const handleSelectionModelChange = useCallback(nextSelectedItems => {
    const normalizedNextSelectedItems = nextSelectedItems
      .map(item => `${item}`)
      .filter(item => item !== 'null' && item !== 'undefined' && item !== '')
    setSelectedItems(prevSelectedItems => {
      if (prevSelectedItems.length !== normalizedNextSelectedItems.length) {
        return normalizedNextSelectedItems
      }

      const prevSelectedItemsSet = new Set(prevSelectedItems.map(item => `${item}`))
      const hasSameItems = normalizedNextSelectedItems.every(item => prevSelectedItemsSet.has(item))
      if (hasSameItems) {
        return prevSelectedItems
      }
      return normalizedNextSelectedItems
    })
  }, [])

  useEffect(() => {
    setPage(0)
  }, [serviceOrderIdSearch])

  useEffect(() => {
    dispatch(getSystemParameters({ name: FINANCE_ASAAS_PAYMENT_METHODS_TOGGLE }))
  }, [dispatch])

  useEffect(() => {
    let isMounted = true

    const loadPersistedDiscounts = async () => {
      if (!selectedDiscountInstallmentIds.length) return
      const itemsById = new Map(items.map(item => [`${item.id}`, item]))

      const discountsById = {}
      await Promise.all(
        selectedDiscountInstallmentIds.map(async installmentId => {
          const installment = itemsById.get(`${installmentId}`)
          if (!isDiscountEnabledForInstallment(installment)) {
            discountsById[installmentId] = {
              anticipationTax: 0,
              anticipationTaxReason: '',
              monthlyDiscount: 0,
              monthlyDiscountReason: '',
              reimbursementDiscount: 0,
              reimbursementDiscountReason: '',
            }
            return
          }

          try {
            const persistedDiscount = await dispatch(getBudgetInstallmentDiscount(installmentId))
            discountsById[installmentId] = {
              anticipationTax: Number(persistedDiscount?.anticipationTax || 0),
              anticipationTaxReason: persistedDiscount?.anticipationTaxReason || '',
              monthlyDiscount: Number(persistedDiscount?.monthlyDiscount || 0),
              monthlyDiscountReason: persistedDiscount?.monthlyDiscountReason || '',
              reimbursementDiscount: Number(persistedDiscount?.reimbursementDiscount || 0),
              reimbursementDiscountReason: persistedDiscount?.reimbursementDiscountReason || '',
            }
          } catch (error) {
            discountsById[installmentId] = discountsById[installmentId] || {
              anticipationTax: 0,
              anticipationTaxReason: '',
              monthlyDiscount: 0,
              monthlyDiscountReason: '',
              reimbursementDiscount: 0,
              reimbursementDiscountReason: '',
            }
          }
        })
      )

      if (!isMounted) return

      setDiscountByInstallmentId(prevState => {
        let hasChanges = false
        const nextState = { ...prevState }

        Object.entries(discountsById).forEach(([installmentId, nextDiscount]) => {
          const previousDiscount = prevState[installmentId]
          const isSameDiscount =
            previousDiscount &&
            previousDiscount.anticipationTax === nextDiscount.anticipationTax &&
            previousDiscount.anticipationTaxReason === nextDiscount.anticipationTaxReason &&
            previousDiscount.monthlyDiscount === nextDiscount.monthlyDiscount &&
            previousDiscount.monthlyDiscountReason === nextDiscount.monthlyDiscountReason &&
            previousDiscount.reimbursementDiscount === nextDiscount.reimbursementDiscount &&
            previousDiscount.reimbursementDiscountReason ===
              nextDiscount.reimbursementDiscountReason

          if (!isSameDiscount) {
            nextState[installmentId] = nextDiscount
            hasChanges = true
          }
        })

        return hasChanges ? nextState : prevState
      })
    }

    loadPersistedDiscounts()

    return () => {
      isMounted = false
    }
  }, [dispatch, isDiscountEnabledForInstallment, itemsSignature, selectedDiscountInstallmentIdsKey])

  const defaultDate = useMemo(
    () => (fromAnticipations ? items?.find(item => item.datetimePaid)?.datetimePaid : null),
    [fromAnticipations, items]
  )

  const handleRemoveNonPermitted = async () => {
    setIsLoading(true)
    await dispatch(getVerifyInstallmentsStepStatus({ ids: formatArrayToString(selectedItems) }))
      .then(res => {
        const { permittedIds = [] } = res
        setSelectedItems(permittedIds)
        showToast({ type: 'success' })
        setIsLoading(false)
      })
      .catch(() => {
        showToast({ type: 'error' })
        setIsLoading(false)
      })
  }

  const handleTitle = () => {
    if (menuType === 'pay') {
      return 'Confirmação do pagamento'
    }
    if (menuType === 'remove') {
      return fromAnticipations ? 'Remover aprovação' : 'Remover pagamento'
    }
    return ''
  }

  return (
    <Grid>
      <Loader hasBackdrop open={isLoading} label="Aguarde..." />
      <HeaderTitle title={handleTitle()}>
        <Grid style={{ display: 'flex', gap: '8px' }}>
          {menuType === 'pay' && (
            <Button variant="ghost" onClick={handleRemoveNonPermitted}>
              Desmarcar não aptas
            </Button>
          )}
          <Button color="red" variant="secondary" onClick={toggleMenu}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="primary"
            onClick={handleConfirmModal}
            disabled={
              selectedItems.length === 0 ||
              (menuType === 'pay' && (hasInvalidDiscount || hasMissingDiscountReason))
            }
          >
            Confirmar
          </Button>
        </Grid>
      </HeaderTitle>
      <Grid className={styles.tableContainer}>
        {menuType === 'remove' && (
          <Alert
            severity="error"
            title="Ao clicar em confirmar, você irá remover os pagamentos selecionados da listagem."
          />
        )}
        <Grid className={styles.confirmationSummaryContainer}>
          <Grid className={styles.confirmationSummaryLeft}>
            <Typography className={styles.confirmationSelectedLabel}>
              Total de itens selecionados:
              <span className={styles.confirmationSelectedValue}> {selectedItems.length}</span>
            </Typography>
            <Grid className={styles.confirmationSearchContainer}>
              <SearchBar
                value={serviceOrderIdSearch}
                onChange={event => setServiceOrderIdSearch(event.target.value)}
                placeholder="Buscar ID do chamado"
                className={styles.confirmationSearchInput}
                InputProps={searchInputProps}
              />
            </Grid>
            {menuType === 'pay' && (hasInvalidDiscount || hasMissingDiscountReason) && (
              <Typography className={styles.discountValidationError}>
                {hasMissingDiscountReason
                  ? 'Informe o motivo para todos os descontos aplicados.'
                  : 'O desconto aplicado não pode ser maior que o total líquido a receber.'}
              </Typography>
            )}
            <Grid className={styles.confirmationSummaryRight}>
              <Typography className={styles.confirmationTotalLabel}>
                Valor total <span>{totalMenuValues?.totalPriceServiceProvider}</span>
              </Typography>
              {showAntecipation && (
                <Typography className={styles.confirmationTotalLabel}>
                  Taxa de antecipação <span>{totalMenuValues?.interestValue}</span>
                </Typography>
              )}
              <Typography className={styles.confirmationTotalLabel}>
                Descontos <span>{totalMenuValues?.totalDiscountsValue}</span>
              </Typography>
              <Typography className={styles.confirmationTotalLabel}>
                Valor líquido a receber <span>{totalMenuValues?.totalValue}</span>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid className={styles.mainTable}>
          <Datagrid
            rows={filteredItems}
            columns={paymentConfirmationColumns({
              styles,
              showAntecipation,
              enableDiscountEdit: menuType === 'pay',
              canEditDiscount: isDiscountEnabledForInstallment,
              onEditDiscount: handleOpenDiscountModal,
            })}
            paginationMode="client"
            autoHeight={filteredItems.length < 10}
            onRowClick={params =>
              window.open(
                `${ROUTES.SERVICE_ORDER}/${params.row.serviceOrderId}/orcamento/${params?.row?.budget}`,
                '_blank',
                'noopener,noreferrer'
              )
            }
            columnVisibilityModel={{ actions: false, __check__: !fromAnticipations }}
            rowCount={filteredItems?.length}
            onPageChange={setPage}
            page={page}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            checkboxSelection
            disableSelectionOnClick={fromAnticipations}
            onSelectionModelChange={handleSelectionModelChange}
            keepNonExistentRowsSelected
            selectionModel={selectedItems}
            sx={{
              fontSize: '1.4rem',
              '& .MuiDataGrid-cell:hover': {
                cursor: 'pointer',
              },
            }}
          />
        </Grid>
      </Grid>
      <MuiDialog
        open={discountModal.open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
          handleCloseDiscountModal()
        }}
        disableEscapeKeyDown
        maxWidth="md"
        PaperProps={{ className: styles.discountModalPaper }}
      >
        <DialogTitle className={styles.titleBlue}>Descontos</DialogTitle>
        <DialogContent className={styles.discountModalContent}>
          {isLoadingDiscountModalData && (
            <Grid
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <CircularProgress size={18} />
              <Typography>Carregando desconto salvo...</Typography>
            </Grid>
          )}
          <Grid className={styles.discountModalGrid}>
            <Grid className={styles.discountModalRow}>
              <MoneyInput
                label="Taxa de antecipação"
                fullWidth
                decimalScale={2}
                defaultValue={0}
                value={
                  discountModalForm.anticipationTax
                    ? formatCurrency(discountModalForm.anticipationTax)
                    : ''
                }
                onChange={event => {
                  setDiscountModalForm(prevState => ({
                    ...prevState,
                    anticipationTax: event?.floatValue ?? null,
                  }))
                  setDiscountModalError('')
                }}
                disabled={isLoadingDiscountModalData || isSavingDiscount}
                placeholder="R$ 0,00"
                className={styles.discountModalField}
              />
              <TextField
                label="Motivo da taxa de antecipação"
                variant="outlined"
                size="small"
                required={discountReasonValidation.required.anticipationTaxReason}
                error={discountReasonValidation.errors.anticipationTaxReason}
                helperText={
                  discountReasonValidation.errors.anticipationTaxReason ? 'Campo obrigatório' : ''
                }
                value={discountModalForm.anticipationTaxReason}
                onChange={event => {
                  setDiscountModalForm(prevState => ({
                    ...prevState,
                    anticipationTaxReason: event.target.value,
                  }))
                  setDiscountModalError('')
                }}
                disabled={isLoadingDiscountModalData || isSavingDiscount}
                className={styles.discountModalReasonField}
              />
            </Grid>
            <Grid className={styles.discountModalRow}>
              <MoneyInput
                label="Desconto de mensalidade"
                fullWidth
                decimalScale={2}
                defaultValue={0}
                value={
                  discountModalForm.monthlyDiscount
                    ? formatCurrency(discountModalForm.monthlyDiscount)
                    : ''
                }
                onChange={event => {
                  setDiscountModalForm(prevState => ({
                    ...prevState,
                    monthlyDiscount: event?.floatValue ?? null,
                  }))
                  setDiscountModalError('')
                }}
                disabled={isLoadingDiscountModalData || isSavingDiscount}
                placeholder="R$ 0,00"
                className={styles.discountModalField}
              />
              <TextField
                label="Motivo do desconto de mensalidade"
                variant="outlined"
                size="small"
                required={discountReasonValidation.required.monthlyDiscountReason}
                error={discountReasonValidation.errors.monthlyDiscountReason}
                helperText={
                  discountReasonValidation.errors.monthlyDiscountReason ? 'Campo obrigatório' : ''
                }
                value={discountModalForm.monthlyDiscountReason}
                onChange={event => {
                  setDiscountModalForm(prevState => ({
                    ...prevState,
                    monthlyDiscountReason: event.target.value,
                  }))
                  setDiscountModalError('')
                }}
                disabled={isLoadingDiscountModalData || isSavingDiscount}
                className={styles.discountModalReasonField}
              />
            </Grid>
            <Grid className={styles.discountModalRow}>
              <MoneyInput
                label="Desconto de reembolso"
                fullWidth
                decimalScale={2}
                defaultValue={0}
                value={
                  discountModalForm.reimbursementDiscount
                    ? formatCurrency(discountModalForm.reimbursementDiscount)
                    : ''
                }
                onChange={event => {
                  setDiscountModalForm(prevState => ({
                    ...prevState,
                    reimbursementDiscount: event?.floatValue ?? null,
                  }))
                  setDiscountModalError('')
                }}
                disabled={isLoadingDiscountModalData || isSavingDiscount}
                placeholder="R$ 0,00"
                className={styles.discountModalField}
              />
              <TextField
                label="Motivo do desconto de reembolso"
                variant="outlined"
                size="small"
                required={discountReasonValidation.required.reimbursementDiscountReason}
                error={discountReasonValidation.errors.reimbursementDiscountReason}
                helperText={
                  discountReasonValidation.errors.reimbursementDiscountReason
                    ? 'Campo obrigatório'
                    : ''
                }
                value={discountModalForm.reimbursementDiscountReason}
                onChange={event => {
                  setDiscountModalForm(prevState => ({
                    ...prevState,
                    reimbursementDiscountReason: event.target.value,
                  }))
                  setDiscountModalError('')
                }}
                disabled={isLoadingDiscountModalData || isSavingDiscount}
                className={styles.discountModalReasonField}
              />
            </Grid>
          </Grid>
          {discountModalError && (
            <Typography className={styles.discountValidationError}>{discountModalError}</Typography>
          )}
        </DialogContent>
        <DialogActions className={styles.discountModalActions}>
          <Button variant="secondary" color="red" onClick={handleCloseDiscountModal}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            color="primary"
            onClick={handleSaveDiscountModal}
            disabled={isSavingDiscount || isLoadingDiscountModalData}
          >
            Salvar
          </Button>
        </DialogActions>
      </MuiDialog>
      {modalOpen && (
        <ConfirmDateAndPaymentModal
          open={modalOpen}
          onConfirm={handleConfirmPayment}
          onCancel={() => setModalOpen(false)}
          defaultDate={defaultDate}
          paymentOptionsOverride={paymentMethodOptions}
        />
      )}
      {dialogMessage?.message && <ConfirmRemoveDialog {...dialogMessage} />}
      {infoDialog.isOpen && (
        <Dialog
          open={infoDialog.isOpen}
          icon={infoDialog.icon}
          type={infoDialog.type}
          subject={infoDialog.subject}
          description={infoDialog.description}
          onApprove={() => setInfoDialog(defaultInfoDialog)}
        />
      )}
    </Grid>
  )
}

export default PaymentConfirmation
